import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import {
  useDivisionDepartments, useLinkDivisionDepartment, useUnlinkDivisionDepartment,
  useDepartments, useDeleteDepartment, errMessage, type Department,
} from './systemMasters';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { DepartmentDetailDialog } from './DepartmentDetailDialog';
import { deptTree, deptTreeOrMatches, toggleIn, treeCell, treeHeader } from './departmentTree';

interface Props {
  companyId?: string;
  /** A not-yet-saved division: the department ids picked so far (applied on the division's Save). */
  staged?: string[];
  onStaged?: (ids: string[]) => void;
}

/**
 * Division detail ▸ Departments tab — the many-to-many dbo.CompanyDepartment links.
 * Mirrors the legacy XAF Departments grid + the "Department" Link picker (a checkbox
 * list of every department with search; OK syncs the links). Columns Department / Parent / Sort.
 */
export function DivisionDepartmentsPanel({ companyId, staged = [], onStaged }: Props) {
  // New division (no id yet): the tab works on the held list instead of the server (G5 parity, 2026-10-14).
  const staging = !companyId && !!onStaged;
  const gated = !companyId && !onStaged;
  const notify = useNotify();
  const { confirm } = useConfirm();
  const { data: serverLinked = [] } = useDivisionDepartments(companyId);
  const { data: all = [] } = useDepartments();
  const link = useLinkDivisionDepartment(companyId ?? '');
  const unlink = useUnlinkDivisionDepartment(companyId ?? '');
  const del = useDeleteDepartment();
  const qc = useQueryClient();

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [pickOpen, setPickOpen] = useState(false);
  const [pickSearch, setPickSearch] = useState('');
  const [checked, setChecked] = useState<Set<string | number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [editDept, setEditDept] = useState<Department | null>(null);   // "{name} - Department" popup (picker edit icon / toolbar Edit)
  // Tree expand/collapse (all expanded to start, as G5) — the linked grid and the picker each keep their own.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [pickCollapsed, setPickCollapsed] = useState<Set<string>>(new Set());

  const linked: Department[] = useMemo(
    () => (staging ? all.filter((x) => staged.includes(x.id)) : serverLinked),
    [staging, all, staged, serverLinked],
  );
  const linkedIds = useMemo(() => new Set(linked.map((d) => d.id)), [linked]);
  const linkedTree = useMemo(() => deptTree(linked, collapsed), [linked, collapsed]);

  const openPick = () => { setChecked(new Set(linkedIds)); setPickSearch(''); setPickOpen(true); };
  // The picker shows the tree; while searching it lists the matches flat.
  const pickTree = useMemo(() => deptTreeOrMatches(all, pickCollapsed, pickSearch), [all, pickSearch, pickCollapsed]);

  // OK syncs: link newly-checked, unlink newly-unchecked (legacy DivisionDepartmentController).
  const applyPick = async () => {
    if (staging) { onStaged!(all.filter((x) => checked.has(x.id)).map((x) => x.id)); setPickOpen(false); return; }
    if (!companyId) return;
    setBusy(true);
    try {
      for (const d of all) {
        const isChecked = checked.has(d.id);
        const wasLinked = linkedIds.has(d.id);
        if (isChecked && !wasLinked) await link.mutateAsync(d.id);
        else if (!isChecked && wasLinked) await unlink.mutateAsync(d.id);
      }
      setPickOpen(false);
    } catch (e) { notify.error(errMessage(e)); }
    finally { setBusy(false); }
  };

  const doUnlink = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one department to unlink.'); return; }
    if (!await confirm({ title: 'Unlink', message: `Remove ${selectedIds.size} department${selectedIds.size === 1 ? '' : 's'} from this division? (The department is kept.)` })) return;
    if (staging) { onStaged!(staged.filter((id) => !selectedIds.has(id))); setSelectedIds(new Set()); return; }
    try { for (const id of selectedIds) await unlink.mutateAsync(String(id)); setSelectedIds(new Set()); }
    catch (e) { notify.error(errMessage(e)); }
  };
  // Delete the department ENTITY (soft-delete) — LIMS Delete on the nested grid; the server's
  // reference guard blocks it if the department still has employees / child departments.
  const doDelete = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one department to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} department${selectedIds.size === 1 ? '' : 's'}? This deletes the department itself.` })) return;
    if (staging) onStaged!(staged.filter((id) => !selectedIds.has(id)));
    try { for (const id of selectedIds) await del.mutateAsync(String(id)); setSelectedIds(new Set()); qc.invalidateQueries({ queryKey: ['sm-division-departments'] }); }
    catch (e) { notify.error(errMessage(e)); }
  };

  // Equal thirds; rows come in G5 tree order (siblings by name), so no column re-sorts them.
  // The rows come in tree order, so the Department column does not re-sort them (it would break the tree).
  const columns: AppColumn<Department>[] = [
    { field: 'name', header: treeHeader('Department', linked.map((d) => d.id), selectedIds, setSelectedIds), headerText: 'Department',
      width: '34%', minWidth: 160, sortable: false, text: (r) => r.name ?? '',
      render: treeCell(linkedTree.meta, collapsed, toggleIn(setCollapsed), selectedIds, setSelectedIds) },
    { field: 'parent', header: 'Parent', width: '33%', minWidth: 140, sortable: false, text: (r) => r.parent ?? '' },
    { field: 'sort', header: 'Sort', width: '33%', minWidth: 70, align: 'right', type: 'number', sortable: false, text: (r) => (r.sort != null ? String(r.sort) : '') },
  ];
  // Link picker: tree Department column + an edit icon on the right that opens the "{name} - Department" popup
  // (G5 parity — owner request 2026-10-14).
  const pickCols: AppColumn<Department>[] = [
    { field: 'name', header: treeHeader('Department', (pickSearch.trim() ? pickTree.rows : all).map((d) => d.id), checked, setChecked),
      headerText: 'Department', minWidth: 360, sortable: false, text: (r) => r.name ?? '',
      render: treeCell(pickTree.meta, pickCollapsed, toggleIn(setPickCollapsed), checked, setChecked) },
    { field: 'edit', header: '', headerText: 'Edit', width: 40, minWidth: 40, sortable: false, filterable: false, align: 'center', text: () => '',
      render: (r) => <button className="pts-icon-btn edit" title="Edit" tabIndex={-1} onClick={(e) => { e.stopPropagation(); setEditDept(r); }}><i className="ti ti-edit" /></button> },
  ];

  // Edit needs exactly one ticked department; it opens the same popup as the picker's edit icon.
  const editTarget = selectedIds.size === 1 ? linked.find((d) => selectedIds.has(d.id)) ?? null : null;
  const canEdit = !gated && editTarget != null;
  const editSelected = () => { if (editTarget) setEditDept(editTarget); };

  useHotkeys([
    { combo: 'alt+l', label: 'Link', group: 'Departments', order: 1, disabled: gated, run: openPick },
    { combo: 'alt+k', label: 'Unlink', group: 'Departments', order: 2, disabled: gated || selectedIds.size === 0, run: () => { void doUnlink(); } },
    // Toolbar Edit opens the ticked department in the "{name} - Department" popup (exactly one row ticked).
    { combo: 'alt+e', label: 'Edit', group: 'Departments', order: 3, disabled: !canEdit, run: editSelected },
    { combo: 'alt+d', label: 'Delete', group: 'Departments', order: 4, disabled: gated || selectedIds.size === 0, run: () => { void doDelete(); } },
  ]);

  if (gated) return <div className="acc-tab-pane"><div style={{ padding: 14, color: '#8a7a66' }}>Save the division first to link departments.</div></div>;

  return (
    <div className="acc-subgrid">
      <div className="acc-sub-toolbar">
        <button className="ca-btn" title={`Link (${formatCombo('alt+l')})`} onClick={openPick}><i className="ti ti-link" /> Link</button>
        <button className="ca-btn" title={`Unlink (${formatCombo('alt+k')})`} onClick={() => void doUnlink()} disabled={selectedIds.size === 0}><i className="ti ti-unlink" /> Unlink</button>
        <button className="ca-btn" title={`Edit (${formatCombo('alt+e')})`} onClick={editSelected} disabled={!canEdit}><i className="ti ti-edit" /> Edit</button>
        <button className="ca-btn" title={`Delete (${formatCombo('alt+d')})`} onClick={() => void doDelete()} disabled={selectedIds.size === 0}><i className="ti ti-trash" /> Delete</button>
        <ExportMenu triggerClassName="ca-btn" filename="Division-Departments" headers={['Department', 'Parent', 'Sort']}
          rows={linked.map((r) => [r.name ?? '', r.parent ?? '', r.sort != null ? String(r.sort) : ''])} />
      </div>
      <AppDataGrid<Department> className="dd-treegrid" rows={linkedTree.rows} columns={columns} getRowId={(r) => r.id}
        selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
        emptyText="No departments linked."
        defaultPageSize={20} />

      {pickOpen && (
        <div className="acc-overlay" onMouseDown={(e) => { e.stopPropagation(); setPickOpen(false); }}>
          <div className="acc-dialog acc-dialog-pick acc-dialog--accent" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr"><span>Department</span><i className="ti ti-x acc-dialog-x" onClick={() => setPickOpen(false)} /></div>
            <div className="acc-dialog-body">
              <div className="acc-pick-search"><div className="acc-search"><i className="ti ti-search" />
                <input autoFocus placeholder="Search…" value={pickSearch} onChange={(e) => setPickSearch(e.target.value)} />
                {pickSearch && <i className="ti ti-x acc-search-x" onClick={() => setPickSearch('')} />}
              </div></div>
              <AppDataGrid<Department> className="dd-treegrid" rows={pickTree.rows} columns={pickCols} getRowId={(r) => r.id}
                selectable selectedIds={checked} onSelectionChange={setChecked}
                emptyText="No departments found."
                defaultPageSize={20} />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void applyPick()} disabled={busy}>OK</button>
              <button className="ca-btn" onClick={() => setPickOpen(false)} disabled={busy}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}

      {editDept && <DepartmentDetailDialog department={editDept} onClose={() => setEditDept(null)} />}
    </div>
  );
}
